import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { AlertTriangle } from 'lucide-react';
import {
  RobbieChromeMessage,
  RobbieContinueLine,
  RobbieDockedPanel,
  RobbieRestingState,
  RobbieVoiceMessage,
} from '../../components/robbie';
import { ChatHandle, ChatMessage } from '../../hooks/useChat';
import { formatRelativeTime, useChatThreadView } from '../../hooks/useChatThreadView';
import { useVoiceRecorder, VoiceClip } from '../../hooks/useVoiceRecorder';
import { RobbieStepContent } from './robbieStepContent';
import { useWizardAccess } from '../../contexts/WizardAccessContext';
import WizardBomProposal from './WizardBomProposal';
import WizardRobbieProposal from './WizardRobbieProposal';
import { BomEditProposal } from '../../types/bomProposal';
import { RobbieProposal } from '../../types/robbieProposal';

/** Robbie-voice copy for the two mic failure modes — never a raw browser error. */
const MIC_FALLBACK_NOTICE: Record<'denied' | 'unsupported', string> = {
  denied: "I couldn't get your mic there — go ahead and type instead.",
  unsupported: "Voice isn't available in this browser yet — type it and I'll get right on it."
};

/**
 * The wizard's right rail: the docked v2 panel chrome wired to the real chat
 * backend. The conversation itself (`chat`, from `useChat`) is owned by the
 * wizard, so a step can send Robbie a message on the buyer's behalf
 * ("Enhance with Robbie") through the same thread this panel renders.
 *
 * An assistant turn whose tool call proposed a change renders its card under
 * its text — live or from history, the same card.
 *
 * How much of the thread opens is decided by `useChatThreadView`: entering an
 * RFQ always opens on Robbie's resting state for the step, with one quiet line
 * back to whatever was said before, however recent that was. Moving between
 * wizard steps is the same visit, so a thread someone unfolded stays unfolded.
 * That choice is presentation only — Robbie's own context is assembled
 * server-side from the stored session, and this app sends him no history to
 * shorten.
 *
 * A viewer on someone else's RFQ reads the conversation and cannot add to it:
 * Robbie's tool calls change RFQ data, so the server refuses a viewer's chat
 * outright and the input would be a box that only ever errors.
 */
export interface WizardRobbieDockedProps {
  rfqId?: string;
  step: number;
  content: RobbieStepContent;
  /** The RFQ's conversation — `useChat`, held by the wizard. */
  chat: ChatHandle;
  /** Robbie-voice status for a stage that is working; suppresses next best step. */
  working?: string;
  /** The wait behind `working` failed; the panel shows its error title instead. */
  workingFailed?: boolean;
  onAction?: (actionId: string) => void;
  className?: string;
  /**
   * Overrides the viewer quiet mode when the server allows this person's chat
   * anyway — the RFQ's pending end user is a VIEWER in the role model, but
   * chatting with Robbie is part of their fill window.
   */
  canChat?: boolean;
}

const Markdown: React.FC<{ children: string }> = ({ children }) => (
  <ReactMarkdown
    components={{
      p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
      strong: ({ node, ...props }) => <strong className="font-bold text-ink" {...props} />,
      ul: ({ node, ...props }) => <ul className="mb-2 list-disc space-y-1 pl-4 last:mb-0" {...props} />,
      ol: ({ node, ...props }) => <ol className="mb-2 list-decimal space-y-1 pl-4 last:mb-0" {...props} />,
      a: ({ node, ...props }) => <a className="text-action hover:underline" {...props} />,
    }}
  >
    {children}
  </ReactMarkdown>
);

/** Robbie's text, then a card for each proposal his tool calls made: a BOM diff or a step card. */
const AssistantTurn: React.FC<{ message: ChatMessage; canApply: boolean }> = ({ message, canApply }) => (
  <>
    <Markdown>{message.content || ''}</Markdown>
    {(message.metadata?.actions ?? []).map((action, i) => {
      if (!action.proposal) return null;
      const storageKey = `${message._id}.${i}`;
      return action.uiEffect === 'PROPOSAL_SHOWN' ? (
        <WizardRobbieProposal key={i} storageKey={storageKey} proposal={action.proposal as RobbieProposal} canApply={canApply} />
      ) : (
        <WizardBomProposal key={i} storageKey={storageKey} proposal={action.proposal as BomEditProposal} canApply={canApply} />
      );
    })}
  </>
);

const WizardRobbieDocked: React.FC<WizardRobbieDockedProps> = ({
  rfqId,
  step,
  content,
  chat,
  working,
  workingFailed,
  onAction,
  className,
  canChat,
}) => {
  const { readOnly: viewerReadOnly, ownerName } = useWizardAccess();
  const readOnly = canChat ? false : viewerReadOnly;
  const [draft, setDraft] = useState('');
  const [micNotice, setMicNotice] = useState<string | null>(null);
  const { messages, chips, isLoading, historyLoaded, isTyping, isFetchingChips, error, sendMessage, sendVoice } = chat;
  // Presentation only: this decides how much of the thread is on screen. What
  // Robbie answers from is built server-side from the stored session — the
  // browser sends no history at all, so folding here cannot narrow his context.
  const thread = useChatThreadView(rfqId, messages, historyLoaded);

  const handleRecordingComplete = useCallback(
    ({ base64, mimeType, url, durationMs, peaks }: VoiceClip) => {
      setMicNotice(null);
      sendVoice(base64, mimeType, url, durationMs, peaks);
    },
    [sendVoice]
  );
  const {
    status: micStatus,
    toggle: toggleMic,
    levels: micLevels,
    elapsedMs: micElapsedMs,
    maxDurationMs: micMaxMs,
  } = useVoiceRecorder({ onRecordingComplete: handleRecordingComplete });

  // Denied permission / unsupported browser degrades to typing, in Robbie's
  // voice — never a raw browser error.
  useEffect(() => {
    if (micStatus === 'denied' || micStatus === 'unsupported') {
      setMicNotice(MIC_FALLBACK_NOTICE[micStatus]);
    }
  }, [micStatus]);

  const send = (text?: string) => {
    const value = (text ?? draft).trim();
    if (!value || isTyping) return;
    sendMessage(value);
    setDraft('');
  };

  const history: RobbieChromeMessage[] = thread.visibleMessages.map((m, i) => {
    const isUser = m.role?.toLowerCase() === 'user';
    // A turn is spoken only if a clip was actually stored for it. Testing the
    // `audio` container itself is not the same question — a reader must never
    // decide "this is a voice message" from a wrapper that could be empty, or
    // typed turns render as silent players. `url` would be wrong the other
    // way: it only exists on a send this session, so history would fall back
    // to text. `mimeType` is set for every stored clip and survives the
    // history read, which drops only the bytes.
    const isVoice = !!m.audio?.mimeType;
    const bubbleContent = isVoice
      ? <RobbieVoiceMessage src={m.audio?.url} durationMs={m.audio?.durationMs} peaks={m.audio?.peaks} />
      : isUser ? m.content : <AssistantTurn message={m} canApply={!readOnly} />;
    return {
      id: m._id || String(i),
      role: isUser ? 'user' : 'robbie',
      content: bubbleContent,
      variant: isVoice ? 'voice' : 'text',
    };
  });

  const chipLabels = chips.length > 0 ? chips : content.prompts;
  const nextStep = content.nextBestStep;

  return (
    <RobbieDockedPanel
      className={className}
      state={micStatus === 'recording' ? 'listening' : isTyping ? 'working' : 'waving'}
      working={working}
      workingFailed={workingFailed}
      nextStep={
        !readOnly && nextStep
          ? {
              label: nextStep.label,
              onSelect: () => nextStep.actionId && onAction?.(nextStep.actionId),
            }
          : undefined
      }
      headerAction={
        // Only offered when there is a conversation to leave behind — a reset
        // with nothing to reset is a control that does nothing.
        !readOnly && history.length > 0
          ? { label: 'New conversation', onSelect: thread.startNewConversation }
          : undefined
      }
      messages={history}
      resting={thread.resting ? <RobbieRestingState greeting={content.restingGreeting} /> : undefined}
      continueLine={
        thread.resting && thread.foldedUntil ? (
          <RobbieContinueLine
            count={thread.foldedCount}
            relativeTime={formatRelativeTime(thread.foldedUntil)}
            onSelect={thread.unfold}
          />
        ) : undefined
      }
      unfoldNonce={thread.unfoldNonce}
      typing={isTyping}
      historyLoading={isLoading && history.length === 0}
      chipsLoading={isFetchingChips && chips.length === 0 && !readOnly}
      notice={
        error ? (
          <div className="flex items-center gap-s2 self-center rounded-control border border-danger-border bg-danger-bg px-s4 py-[6px] text-[10.5px] font-medium text-danger-ink">
            <AlertTriangle className="h-s3 w-s3 shrink-0" />
            {error}
          </div>
        ) : (
          micNotice && (
            <div className="flex items-center gap-s2 self-center rounded-control border border-neutral-border bg-neutral-bg px-s4 py-[6px] text-[10.5px] font-medium text-neutral-ink">
              {micNotice}
            </div>
          )
        )
      }
      quickActions={readOnly ? [] : chipLabels.map((label, i) => ({ id: `${step}-${i}`, label, onSelect: () => send(label) }))}
      footerNote={
        readOnly
          ? ownerName
            ? `This is ${ownerName}'s RFQ — you're reading along. Ask ${ownerName} if you'd like something changed.`
            : "This RFQ belongs to someone else on your team — you're reading along. Ask them if you'd like something changed."
          : undefined
      }
      input={{
        value: draft,
        onChange: setDraft,
        onSend: () => send(),
        onMic: toggleMic,
        recording: micStatus === 'recording',
        recordingLevels: micLevels,
        recordingElapsedMs: micElapsedMs,
        recordingMaxMs: micMaxMs,
        disabled: !rfqId,
        sendDisabled: isTyping,
        placeholder: isTyping ? 'Ask a follow-up…' : 'Ask Robbie…',
      }}
    />
  );
};

export default WizardRobbieDocked;
